import { useState, useEffect, Fragment, useRef } from 'react';
import MissionService from '../../../../../service/MissionService';
import DefaultSatModel from "../../../../CommonComponents/DefaultSatModel";
import AnimationLoader from "./loading_model.json"
import CustomToolTip from '../../../../CommonComponents/CustomToolTip';
import { useParams } from 'react-router-dom';
import ConfirmationDialog from '../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { setLightingInCanvas, showToastMessage, extract3DFilesFromZip, extractAndLoadModelFromZipUrl, loadGltfFromUrl, loadGltfModelWithMap } from '../../../../../utils/commonFunctions';
import CommonCanvas from '../../../../CommonComponents/CommonCanvas';
import './FileUploader.scss'
import Lottie from 'react-lottie-player';

const FileUploader = ({ Satellite, busSizeUsage, payloadSizeUsage, isCloneTemplate, satelliteModelUploader, setSatelliteModelUploader, model, setModel }) => {
    const [loading, setLoading] = useState<boolean | string>(false);
    const params = useParams();
    const { mission_id } = params;
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [models, setModels] = useState<any>();
    // Tracks preview blob URLs so they can be revoked and not leak memory.
    const previewObjectUrlsRef = useRef<string[]>([]);

    // Registers a blob URL for later cleanup.
    const trackPreviewObjectUrl = (url: string) => {
        previewObjectUrlsRef.current.push(url);
    };

    // Revokes provided URLs (or all tracked URLs) and removes them from tracking.
    const revokePreviewObjectUrls = (urls?: string[]) => {
        const urlsToRevoke = urls || previewObjectUrlsRef.current;
        urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
        previewObjectUrlsRef.current = previewObjectUrlsRef.current.filter((url) => !urlsToRevoke.includes(url));
    };

    // Ensures preview URLs are cleaned when this viewer unmounts.
    useEffect(() => {
        return () => {
            revokePreviewObjectUrls();
        };
    }, []);

    useEffect(() => {
        if (!model?.modelUrl) return;
        // Revoke stale local preview URLs before loading the next model.
        revokePreviewObjectUrls();
        const fileName = model.modelUrl.split('?')[0].split('/').pop() || '';
        const isZipFile = fileName?.toLowerCase().endsWith('.zip')
        const isZip = model?.modelName?.toLowerCase().endsWith('.zip') || model.zip || isZipFile;
        const isGlbModel = model?.modelName?.toLowerCase().endsWith('.glb');
        setLoading(true);
        if (isZip) {
            if (model?.zip) {
                extractAndLoadModelFromZip(model?.zip)
            } else {
                handleZipModelFromUrl(model.modelUrl);
            }
        } else if (isGlbModel) {
            // load .glb model
            loadModel(model.modelUrl);
        }
    }, [model.modelUrl, model.zip]);

    const loadModel = (modelUrl: string) => {
        loadGltfFromUrl(modelUrl, { targetSize: 2 }).then((scene) => {
            setModels(scene);
            setLoading(false);
        });
    };

    const extractAndLoadModelFromZip = async (zip: string | File | Blob) => {
        const extractedFiles = await extract3DFilesFromZip(zip)
        if (!extractedFiles || extractedFiles.length === 0) return;
        const mainFile = extractedFiles.find(
            file => file.name.endsWith(".glb") || file.name.endsWith(".gltf")
        );
        setModel({ ...model, modelName: mainFile?.name });
        if (!mainFile) return;
        // Build blob URL map so LoadingManager can resolve .bin refs
        const map: Record<string, string> = {};
        const createdUrls: string[] = [];
        extractedFiles.forEach(file => {
            const objectUrl = URL.createObjectURL(file);
            map[file.name] = objectUrl;
            createdUrls.push(objectUrl);
            trackPreviewObjectUrl(objectUrl);
        });
        const mainBlobUrl = map[mainFile.name];

        setLoading(true);
        // Always revoke temporary extracted-file URLs after the preview load attempt completes.
        try {
            const scene = await loadGltfModelWithMap(mainBlobUrl, map, { targetSize: 2 });
            setModels(scene);
        } finally {
            revokePreviewObjectUrls(createdUrls);
            setLoading(false);
        }
    };
    const handleZipModelFromUrl = async (url: string) => {
        setLoading(true);
        const result = await extractAndLoadModelFromZipUrl(url, { targetSize: 2 });
        if (result?.scene) {
            setModel({ ...model, modelName: result.mainFileName });
            setModels(result.scene);
        }
        setLoading(false);
    };

    const reloadDefaultModel = () => {
        setLoading(true);
        MissionService.deleteSatelliteBucket(Satellite.satellite_Id, mission_id).then(() => {
            setModels(null);
            setModel({ modelName: null, modelUrl: null, model: null });
        }).catch((error) => {
            showToastMessage('error', error?.response?.data?.message || "Something went wrong while deleting model");
        }).finally(() => {
            setLoading(false);
        });
    };

    return (
        <Fragment>
            <div className='file_uploader_container'>
                {(!model?.modelName && !loading) &&
                    <div
                        onClick={() => { setSatelliteModelUploader(true) }}
                        className={`empty_model_container`}
                    >
                        <div className='model_button_style'>
                            <img src={require('../../../../../assets/images/svgs/upload_model.svg').default} alt="Upload Model" className='img_container' />
                        </div>
                        <span>3D Model</span>
                    </div>
                }

                {model?.modelName && !loading &&
                    <div className={`model_container`}>
                        <div className='model_container_content'>
                            <div
                                className='model_icon_container'
                                title={model?.modelName}
                            >
                                <img
                                    src={require("../../../../../assets/images/svgs/satModelIcon.svg").default}
                                    alt="3D Model"
                                    style={{ opacity: 0.6, width: "20px", height: "20px" }}
                                />
                                    <span className="span">
                                        {model?.modelName}
                                    </span>
                                    <div className='replace_model_container' >
                                    <CustomToolTip title='Replace Model' placement='top'>
                                        <div
                                            id='reLoadModel'
                                            className='loading_model_class'
                                            onClick={() => { setSatelliteModelUploader(true) }}
                                        >
                                            <img
                                                src={require('../../../../../assets/images/svgs/recomputeModel.svg').default}
                                                alt="3D Model"
                                                style={{ width: 'var(--24, 24px)', height: 'var(--24, 24px)' }}
                                            />
                                        </div>
                                    </CustomToolTip>
                                    <CustomToolTip title='Delete Model' placement='top'>
                                        <div
                                            id='deleteModel'
                                            className='delete_model_class'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDeleteModal(true);
                                            }}
                                        >
                                            <img
                                                src={require('../../../../../assets/images/svgs/deleteModel.svg').default}
                                                alt="3D Model"
                                                style={{ width: 'var(--16, 16px)', height: 'var(--16, 16px)' }}
                                            />
                                        </div>
                                    </CustomToolTip>
                                </div>
                            </div>
                        </div>
                    </div>
                }

                <ConfirmationDialog
                    type='delete'
                    open={openDeleteModal ? true : false}
                    title={`Delete 3D Model?`}
                    description={`Deleting 3D Model will permanently remove custom model from the database, and default model will appear in place.`}
                    onCancel={() => setOpenDeleteModal(false)}
                    onConfirm={() => {
                        reloadDefaultModel()
                        setOpenDeleteModal(false);
                    }}
                    confirm_button_text={`Delete`}
                />
                {(models && !loading) &&
                    <CommonCanvas
                        model={models}
                        controls={{ minDistance: 5, maxDistance: 100, target: [0, 0, 0] }}
                        setLightingInCanvas={setLightingInCanvas}
                        onReplaceModel={null}
                        modelName={model?.modelName}
                    />
                }
                {
                    (!models && !loading && !satelliteModelUploader) &&
                    <DefaultSatModel
                        busSize={Satellite.Template?.['template-attribute']?.['bus-type']}
                        size={busSizeUsage + payloadSizeUsage}
                        canvas_height="100%"
                        canvas_width="100%"
                        isPayloadChanges="false"
                        cubeSize={isCloneTemplate ? 0 : 1} />
                }

                {loading &&
                    <div className='model_loader'>
                        <Lottie
                            key={String(loading)}
                            loop
                            animationData={AnimationLoader}
                            play
                        />
                    </div>
                }
            </div>
        </Fragment>
    );
}
export default FileUploader;